var _a;
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Button } from "../components/Button";
import { Chip } from "../components/Chip";
import { Icon } from "../components/Icon";
import { KeyboardShortcut, } from "../components/KeyboardShortcut";
import { LinkWrapper, } from "../components/LinkWrapper";
import { radioIndicatorStyles, radioStyles, } from "../components/RadioGroup";
import { ScrollArea } from "../components/ScrollArea";
import { SearchInput, } from "../components/SearchInput";
import { Tooltip } from "../components/Tooltip";
import { useSheetContainer } from "../hooks/useSheetContainer";
import { Check, ChevronRight } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
import { useMemo, useRef } from "react";
const ITEM_VARIANTS = ["default", "warning"];
export const menuStyleClasses = {
    inset: "s-pl-8",
    container: cn("s-rounded-xl s-border-hovering s-p-1", "s-border s-border-border dark:s-border-border-night", "s-bg-background dark:s-bg-muted-background-night", "s-text-foreground dark:s-text-foreground-night", "s-z-50 s-min-w-[8rem]", "data-[state=open]:s-animate-in data-[state=closed]:s-animate-out data-[state=closed]:s-fade-out-0 data-[state=open]:s-fade-in-0 data-[state=closed]:s-zoom-out-95 data-[state=open]:s-zoom-in-95 data-[side=bottom]:s-slide-in-from-top-2 data-[side=left]:s-slide-in-from-right-2 data-[side=right]:s-slide-in-from-left-2 data-[side=top]:s-slide-in-from-bottom-2"),
    item: cva(cn("s-relative s-flex s-gap-2 s-cursor-pointer s-select-none s-items-center s-outline-none s-rounded-lg s-heading-sm s-transition-colors data-[disabled]:s-pointer-events-none", "data-[disabled]:s-text-primary-400 dark:data-[disabled]:s-text-primary-400-night"), {
        variants: {
            variant: {
                default: cn("s-p-2", "data-[highlighted]:s-text-foreground dark:data-[highlighted]:s-text-foreground-night", "data-[highlighted]:s-bg-muted-background dark:data-[highlighted]:s-bg-muted-night"),
                tags: cn("s-p-0.5", "data-[highlighted]:s-text-foreground dark:data-[highlighted]:s-text-foreground-night", "data-[highlighted]:s-bg-muted-background dark:data-[highlighted]:s-bg-muted-night"),
                warning: cn("s-p-2", "s-text-warning-500 dark:s-text-warning-500-night", "data-[highlighted]:s-bg-warning-50 dark:data-[highlighted]:s-bg-warning-50-night", "active:s-bg-warning-100 dark:active:s-bg-warning-100-night"),
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }),
    subTrigger: {
        default: cn("s-mr-1 s-ml-auto s-tracking-widest", "s-text-primary-400 dark:s-text-primary-400-night"),
        span: "s-absolute s-left-2 s-flex s-h-3.5 s-w-3.5 s-items-center s-justify-center",
    },
    label: cn("s-px-2 s-py-2 s-heading-xs", "s-text-muted-foreground dark:s-text-muted-foreground-night"),
    description: cn("s-grow s-truncate s-text-xs s-font-normal", "s-text-muted-foreground dark:s-text-muted-foreground-night"),
    separator: cn("-s-mx-1 s-my-1 s-h-px", "s-bg-separator dark:s-bg-separator-night"),
    shortcut: "s-ml-auto",
};
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownItemRegistryContext = React.createContext(null);
const renderIcon = (icon, size = "xs", variant) => {
    // If it's a React element (already rendered), return it as is
    if (React.isValidElement(icon)) {
        return icon;
    }
    // For any component type (including exotic components), render it with Icon
    if (typeof icon === "function" || typeof icon === "object") {
        return (React.createElement(Icon, { size: size, visual: icon, className: variant === "warning"
                ? undefined
                : "s-text-muted-foreground dark:s-text-muted-foreground-night" }));
    }
    // For primitive values, return null
    return null;
};
const ItemWithLabelIconAndDescription = ({ label, icon, description, truncate, children, endComponent, variant, }) => {
    return (React.createElement(React.Fragment, null,
        label && (React.createElement("div", { className: cn("s-grid s-flex-grow s-items-center s-gap-x-2.5", icon && endComponent
                ? "s-grid-cols-[auto,1fr,auto]"
                : icon
                    ? "s-grid-cols-[auto,1fr]"
                    : endComponent
                        ? "s-grid-cols-[1fr,auto]"
                        : "s-grid-cols-[1fr]") },
            renderIcon(icon, "sm", variant),
            React.createElement("div", { className: cn("s-flex s-flex-col", truncate && "s-truncate") },
                React.createElement("span", { className: cn(truncate ? "s-truncate" : "s-line-clamp-3") }, label),
                description && (React.createElement("span", { className: cn("s-text-xs s-font-normal s-text-muted-foreground dark:s-text-muted-foreground-night", truncate ? "s-truncate" : "s-line-clamp-3") }, description))),
            endComponent)),
        children));
};
const DropdownMenuSubTrigger = React.forwardRef(({ className, label, icon, children, inset, ...props }, ref) => (React.createElement(DropdownMenuPrimitive.SubTrigger, { ref: ref, className: cn(menuStyleClasses.item({ variant: "default" }), inset ? menuStyleClasses.inset : "", className), ...props },
    React.createElement(ItemWithLabelIconAndDescription, { label: label, icon: icon, endComponent: React.createElement(Icon, { size: "xs", visual: ChevronRight, className: "s-text-muted-foreground dark:s-text-muted-foreground-night" }) }, children))));
DropdownMenuSubTrigger.displayName =
    DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = React.forwardRef(({ className, children, dropdownHeaders, ...props }, ref) => (React.createElement(DropdownMenuPrimitive.SubContent, { ref: ref, className: cn(menuStyleClasses.container, "s-flex s-flex-col s-p-0 s-shadow-lg", dropdownHeaders && "s-h-80 xs:s-h-96", className), ...props },
    dropdownHeaders && (React.createElement("div", { className: "s-sticky s-top-0 s-bg-background dark:s-bg-muted-background-night" }, dropdownHeaders)),
    React.createElement(ScrollArea, { className: "s-w-full s-flex-1", hideScrollBar: false, orientation: "vertical", viewportClassName: cn("s-flex-1", "s-max-h-[calc(var(--radix-dropdown-menu-content-available-height)-var(--header-height,20px))]") }, children))));
DropdownMenuSubContent.displayName =
    DropdownMenuPrimitive.SubContent.displayName;
const nativeInputValueSetter = typeof window !== "undefined"
    ? (_a = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")) === null || _a === void 0 ? void 0 : _a.set
    : undefined;
const SEARCHABLE_MENU_ITEM_SELECTOR = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';
const OPEN_SEARCHABLE_MENU_ITEM_SELECTOR = [
    '[data-radix-menu-content][data-state=open] [role="menuitem"]',
    '[data-radix-menu-content][data-state=open] [role="menuitemcheckbox"]',
    '[data-radix-menu-content][data-state=open] [role="menuitemradio"]',
].join(", ");
function getFirstDropdownMenuItem(container) {
    return container.querySelector(SEARCHABLE_MENU_ITEM_SELECTOR);
}
function getFirstOpenDropdownMenuItem() {
    return document.querySelector(OPEN_SEARCHABLE_MENU_ITEM_SELECTOR);
}
function getDropdownSearchInput(container) {
    var _a;
    return ((_a = container.querySelector('[data-dropdown-searchbar] input[type="text"]')) !== null && _a !== void 0 ? _a : container.querySelector('input[type="text"]'));
}
function resolveDropdownSearchInput(searchInputRef, container) {
    var _a;
    return (_a = searchInputRef === null || searchInputRef === void 0 ? void 0 : searchInputRef.current) !== null && _a !== void 0 ? _a : getDropdownSearchInput(container);
}
function isDropdownTextEntryElement(element) {
    return (element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement);
}
function setDropdownSearchInputValue(input, nextValue) {
    if (!nativeInputValueSetter) {
        return;
    }
    // Use the native setter so React sees the value change before the input event.
    nativeInputValueSetter.call(input, nextValue);
    input.dispatchEvent(new Event("input", { bubbles: true }));
}
const DropdownMenuContent = React.forwardRef(({ className, sideOffset = 4, mountPortal = true, mountPortalContainer, dropdownHeaders, highlightedItemId, preventAutoFocusOnClose = true, onCloseAutoFocus, onKeyDownCapture, onKeyDown, searchInputRef, scrollHighlightedItemIntoView = false, children, ...props }, ref) => {
    const viewportRef = useRef(null);
    const itemElementsRef = useRef(new Map());
    const handleKeyDownCapture = (e) => {
        onKeyDownCapture === null || onKeyDownCapture === void 0 ? void 0 : onKeyDownCapture(e);
        if (e.defaultPrevented) {
            return;
        }
        if (e.key !== "ArrowUp") {
            return;
        }
        const input = resolveDropdownSearchInput(searchInputRef, e.currentTarget);
        const firstItem = getFirstDropdownMenuItem(e.currentTarget);
        if (!input || !firstItem || document.activeElement !== firstItem) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        input.focus();
    };
    const handleKeyDown = (e) => {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        if (e.defaultPrevented) {
            return;
        }
        if (isDropdownTextEntryElement(document.activeElement)) {
            return;
        }
        const input = resolveDropdownSearchInput(searchInputRef, e.currentTarget);
        if (!input) {
            return;
        }
        // Treat single-character keys as search input
        if (e.key.length === 1) {
            e.preventDefault();
            input.focus();
            setDropdownSearchInputValue(input, input.value + e.key);
        }
        else if (e.key === "Backspace" && input.value.length > 0) {
            e.preventDefault();
            input.focus();
            setDropdownSearchInputValue(input, input.value.slice(0, -1));
        }
    };
    const handleCloseAutoFocus = React.useCallback((event) => {
        if (preventAutoFocusOnClose) {
            event.preventDefault();
        }
        onCloseAutoFocus === null || onCloseAutoFocus === void 0 ? void 0 : onCloseAutoFocus(event);
    }, [preventAutoFocusOnClose, onCloseAutoFocus]);
    const registerItem = React.useCallback((itemId, element) => {
        if (element) {
            itemElementsRef.current.set(itemId, element);
        }
        else {
            itemElementsRef.current.delete(itemId);
        }
    }, []);
    const itemRegistryContextValue = useMemo(() => ({ registerItem }), [registerItem]);
    React.useEffect(() => {
        if (!scrollHighlightedItemIntoView || !highlightedItemId) {
            return;
        }
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }
        const highlightedItem = itemElementsRef.current.get(highlightedItemId);
        highlightedItem === null || highlightedItem === void 0 ? void 0 : highlightedItem.scrollIntoView({ block: "nearest" });
    }, [highlightedItemId, scrollHighlightedItemIntoView]);
    const content = (React.createElement(DropdownMenuPrimitive.Content, { ref: ref, sideOffset: sideOffset, onKeyDownCapture: handleKeyDownCapture, onKeyDown: handleKeyDown, className: cn(menuStyleClasses.container, "s-flex s-flex-col s-p-0 s-shadow-md", dropdownHeaders && "s-h-80 xs:s-h-96", // We use dropdownHeaders for putting search bar, so we can set the height for the container
        className), onCloseAutoFocus: handleCloseAutoFocus, ...props },
        React.createElement("div", { className: "s-sticky s-top-0 s-bg-background dark:s-bg-muted-background-night" }, dropdownHeaders && dropdownHeaders),
        React.createElement(ScrollArea, { className: "s-w-full s-flex-1", viewportClassName: cn("s-flex-1", "s-max-h-[calc(var(--radix-dropdown-menu-content-available-height)-var(--header-height,20px))]"), viewportRef: viewportRef },
            React.createElement(DropdownItemRegistryContext.Provider, { value: itemRegistryContextValue }, children))));
    const container = useSheetContainer(mountPortalContainer);
    return mountPortal ? (React.createElement(DropdownMenuPrimitive.Portal, { container: container }, content)) : (content);
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
const DropdownMenuItem = React.forwardRef(({ children, variant, description, className, inset, icon, itemId, truncateText, label, href, target, rel, asChild, replace, shallow, prefetch, endComponent, tooltip, ...props }, ref) => {
    const dropdownItemRegistry = React.useContext(DropdownItemRegistryContext);
    const handleItemRef = React.useCallback((element) => {
        if (typeof ref === "function") {
            ref(element);
        }
        else if (ref) {
            ref.current = element;
        }
        if (itemId) {
            dropdownItemRegistry === null || dropdownItemRegistry === void 0 ? void 0 : dropdownItemRegistry.registerItem(itemId, element);
        }
    }, [dropdownItemRegistry, itemId, ref]);
    const item = (React.createElement(LinkWrapper, { href: href, target: target, rel: rel, replace: replace, shallow: shallow, prefetch: prefetch },
        React.createElement(DropdownMenuPrimitive.Item, { ref: handleItemRef, className: cn(menuStyleClasses.item({ variant }), inset ? menuStyleClasses.inset : "", className), ...props, asChild: asChild },
            React.createElement("div", { className: "s-h-full s-w-full" },
                React.createElement(ItemWithLabelIconAndDescription, { label: label, icon: icon, description: description, truncate: truncateText, endComponent: endComponent, variant: variant }, children)))));
    const itemWithTooltip = tooltip ? (React.createElement(Tooltip, { tooltipTriggerAsChild: true, label: tooltip, trigger: React.createElement("span", { className: "s-block s-w-full" }, item) })) : (item);
    return itemWithTooltip;
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuCheckboxItem = React.forwardRef(({ className, children, description, label, icon, truncateText, ...props }, ref) => (React.createElement(DropdownMenuPrimitive.CheckboxItem, { ref: ref, className: cn(menuStyleClasses.item({ variant: "default" }), menuStyleClasses.inset), ...props },
    React.createElement("span", { className: menuStyleClasses.subTrigger.span },
        React.createElement(DropdownMenuPrimitive.ItemIndicator, null,
            React.createElement(Icon, { size: "xs", visual: Check, className: "s-text-muted-foreground dark:s-text-muted-foreground-night" }))),
    React.createElement(ItemWithLabelIconAndDescription, { label: label, icon: icon, description: description, truncate: truncateText }, children))));
DropdownMenuCheckboxItem.displayName =
    DropdownMenuPrimitive.CheckboxItem.displayName;
const DropdownMenuRadioItem = React.forwardRef(({ className, children, description, label, icon, endComponent, ...props }, ref) => (React.createElement(DropdownMenuPrimitive.RadioItem, { ref: ref, className: cn(menuStyleClasses.item({ variant: "default" }), menuStyleClasses.inset, "s-group/dropdown-radio", className), ...props },
    React.createElement("span", { className: cn("s-absolute s-left-2", radioStyles({ size: "xs" })) },
        React.createElement(DropdownMenuPrimitive.ItemIndicator, null,
            React.createElement("div", { className: radioIndicatorStyles({ size: "xs" }) }))),
    React.createElement(ItemWithLabelIconAndDescription, { label: label, icon: icon, description: description, endComponent: endComponent }, children))));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
const DropdownMenuTagItem = React.forwardRef(({ label, size = "xs", color = "primary", icon, onRemove, className, onClick, ...props }, ref) => {
    return (React.createElement(DropdownMenuPrimitive.Item, { ref: ref, className: cn(menuStyleClasses.item({ variant: "tags" }), className), ...props },
        React.createElement(Chip, { label: label, size: size, color: color, onRemove: onRemove, onClick: onClick, icon: icon })));
});
DropdownMenuTagItem.displayName = "DropdownMenuTagItem";
const DropdownMenuTagList = React.forwardRef(({ children, className }, ref) => {
    return (React.createElement("div", { ref: ref, className: cn("s-flex s-flex-wrap", className) }, children));
});
DropdownMenuTagList.displayName = "DropdownMenuTagList";
const DropdownMenuLabel = React.forwardRef(({ children, className, inset, label, ...props }, ref) => (React.createElement(DropdownMenuPrimitive.Label, { ref: ref, className: cn(menuStyleClasses.label, inset ? menuStyleClasses.inset : "", className), ...props },
    label && React.createElement(React.Fragment, null, label),
    children)));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (React.createElement(DropdownMenuPrimitive.Separator, { ref: ref, className: cn(menuStyleClasses.separator, className), ...props })));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
const DropdownMenuShortcut = ({ className, shortcut, children, ...props }) => {
    const resolvedShortcut = shortcut !== null && shortcut !== void 0 ? shortcut : "";
    if (!resolvedShortcut && children) {
        return (React.createElement("span", { className: cn(className), ...props }, children));
    }
    return (React.createElement(KeyboardShortcut, { shortcut: resolvedShortcut, className: cn(menuStyleClasses.shortcut, className), ...props }));
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";
const DropdownMenuSearchbar = React.forwardRef(({ placeholder, value, onChange, onKeyDown, name, className, disabled = false, isLoading = false, button, autoFocus, }, ref) => {
    const internalRef = useRef(null);
    React.useImperativeHandle(ref, () => internalRef.current);
    React.useEffect(() => {
        if (autoFocus) {
            setTimeout(() => {
                var _a;
                (_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 0);
        }
    }, [autoFocus]);
    const handleKeyDown = (e) => {
        e.stopPropagation();
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        if (!e.defaultPrevented) {
            if (e.key === "Enter") {
                e.preventDefault();
                const firstItem = getFirstOpenDropdownMenuItem();
                if (firstItem instanceof HTMLElement) {
                    firstItem.click();
                }
            }
            if (e.key === "Tab" || e.key === "ArrowDown") {
                e.preventDefault();
                const firstItem = getFirstOpenDropdownMenuItem();
                if (firstItem instanceof HTMLElement) {
                    firstItem.focus();
                }
            }
        }
    };
    return (React.createElement("div", { className: cn("s-flex s-gap-1.5 s-p-1.5", className), "data-dropdown-searchbar": true },
        React.createElement(SearchInput, { className: "s-w-full", ref: internalRef, placeholder: placeholder, name: name, value: value, onChange: onChange, onKeyDown: handleKeyDown, disabled: disabled, isLoading: isLoading }),
        button));
});
DropdownMenuSearchbar.displayName = "DropdownMenuSearchbar";
const DropdownMenuFiltersInner = ({ filters, selectedValues = [], onSelectFilter, className, }, ref) => {
    const multiSelectionValues = Array.isArray(selectedValues)
        ? selectedValues
        : [];
    return (React.createElement("div", { ref: ref, className: cn("s-flex s-flex-wrap s-gap-0.5 s-p-2", className) }, filters.map((filter) => {
        const isSelected = multiSelectionValues.includes(filter.value);
        return (React.createElement(Button, { key: filter.value, size: "xs", variant: isSelected ? "primary" : "outline", label: filter.label, onClick: () => onSelectFilter(filter.value) }));
    })));
};
const DropdownMenuFilters = React.forwardRef(DropdownMenuFiltersInner);
DropdownMenuFilters.displayName = "DropdownMenuFilters";
const DropdownTooltip = ({ description, media }) => (React.createElement("div", { className: "s-space-y-4" },
    media && React.createElement("div", { className: "s-rounded-sm" }, media),
    React.createElement("p", { className: "text-foreground dark:text-foreground-night s-text-sm s-font-normal" }, description)));
DropdownTooltip.displayName = "DropdownTooltip";
const DropdownTooltipTrigger = React.forwardRef(({ children, className, description, media, mountPortal = true, mountPortalContainer, onVisibilityChange, side = "right", sideOffset = 8, }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const handleOpenChange = React.useCallback((open) => {
        setIsOpen(open);
        onVisibilityChange === null || onVisibilityChange === void 0 ? void 0 : onVisibilityChange(open);
    }, [onVisibilityChange]);
    React.useEffect(() => {
        var _a;
        if (!isOpen) {
            return;
        }
        const closeTooltip = () => {
            handleOpenChange(false);
        };
        window.addEventListener("scroll", closeTooltip, true);
        (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.addEventListener("scroll", closeTooltip);
        return () => {
            var _a;
            window.removeEventListener("scroll", closeTooltip, true);
            (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.removeEventListener("scroll", closeTooltip);
        };
    }, [handleOpenChange, isOpen]);
    const tooltipContent = (React.createElement(TooltipPrimitive.Content, { side: side, sideOffset: sideOffset, className: cn(menuStyleClasses.container, "s-w-48 s-max-w-sm s-p-2 s-shadow-lg") },
        React.createElement(DropdownTooltip, { description: description, media: media })));
    const container = useSheetContainer(mountPortalContainer);
    return (React.createElement(TooltipPrimitive.Provider, { delayDuration: 700 },
        React.createElement(TooltipPrimitive.Root, { open: isOpen, onOpenChange: handleOpenChange },
            React.createElement(TooltipPrimitive.Trigger, { asChild: true, className: className, ref: ref },
                React.createElement("span", { className: "s-block s-w-full" }, children)),
            mountPortal ? (React.createElement(TooltipPrimitive.Portal, { container: container }, tooltipContent)) : (tooltipContent))));
});
DropdownTooltipTrigger.displayName = "DropdownTooltipTrigger";
const DropdownMenuStaticItem = React.forwardRef(({ label, value, children, className }, ref) => (React.createElement("div", { ref: ref, className: cn("s-flex s-h-9 s-items-center s-gap-2 s-px-2 s-text-sm", "s-text-foreground dark:s-text-foreground-night", className) },
    React.createElement("span", { className: "s-grow s-font-medium" }, label),
    value && (React.createElement("span", { className: cn("s-shrink-0", "s-text-muted-foreground dark:s-text-muted-foreground-night") }, value)),
    children && React.createElement("div", { className: "s-shrink-0" }, children))));
DropdownMenuStaticItem.displayName = "DropdownMenuStaticItem";
export { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuFilters, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSearchbar, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuStaticItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTagItem, DropdownMenuTagList, DropdownMenuTrigger, DropdownTooltipTrigger, };
//# sourceMappingURL=Dropdown.js.map